import type { AnyObject } from "@entrilia/shared/types.ts";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type {
  ConditionConfiguration,
  FieldConfiguration,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  BuildListReportRequest,
  ListDataSettings,
  ReportField,
} from "../../../../../../../../../api/types/svTypes.ts";
import useDeferredRequest from "../../../../../../hooks/useDeferredRequest.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch.ts";
import {
  selectCurrentReportId,
  selectRefreshToken,
  selectSessionId,
} from "../../../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../../../store/selectors/devToolsSelectors.ts";
import {
  selectConditions,
  selectListFields,
  selectListLedger,
  selectListSettingsState,
  selectSorts,
} from "../../../../../store/selectors/fieldsStateSelectors.ts";
import { createCurrentReportAction } from "../../../../../store/utils/storeUtils.ts";
import { areConfigurationsEqual } from "../../../../../utilities/configurationComparer.ts";
import { hasConditionValues } from "../../../../../utilities/getValidConditions.ts";
import {
  isListConfigurationValid,
  type ConfigurationValidReturnType,
} from "../../../../../utilities/isConfigurationValid.ts";
import { useIsAddingFields } from "../../../common/fields/useFieldDestination.ts";

export interface ListBuildPayload extends AnyObject {
  refreshToken: string;
  reportId: string | undefined;
  sessionId: string;
  conditions: ConditionConfiguration[];
  fields: FieldConfiguration[];
  ledger?: string;
  settings: ListDataSettings;
  useQueryLogging: boolean;
}

export interface ListPreviewConfiguration {
  fields: ReportField[];
  payload: ListBuildPayload;
  validity: ConfigurationValidReturnType;
}

interface ListReportConfigurationResult {
  activeConfiguration: ListPreviewConfiguration;
  validity: ConfigurationValidReturnType;
  buildProgress: number | undefined;
  exportRequestConfig: BuildListReportRequest;
}

const useListReportConfiguration = (): ListReportConfigurationResult => {
  const isAdding = useIsAddingFields();
  const dispatch = useAppDispatch();
  const conditionFields = useSelector(selectConditions);
  const listFields = useSelector(selectListFields);
  const sortFields = useSelector(selectSorts);
  const settings = useSelector(selectListSettingsState);
  const ledger = useSelector(selectListLedger);
  const reportId = useSelector(selectCurrentReportId);
  const refreshToken = useSelector(selectRefreshToken);
  const sessionId = useSelector(selectSessionId);
  const logQueries = useSelector(selectLogQueries);

  const dataSettings = useMemo(
    (): ListDataSettings => ({
      showGrandTotal: settings.showGrandTotal,
      hideBlankRows: settings.hideBlankRows && settings.hideBlankRowsColumnIds.length > 0,
      hideBlankRowsColumnIds: settings.hideBlankRowsColumnIds,
    }),
    [settings.showGrandTotal, settings.hideBlankRows, settings.hideBlankRowsColumnIds]
  );

  const payload = useMemo(
    (): ListBuildPayload => ({
      refreshToken,
      reportId,
      sessionId,
      conditions: conditionFields.filter(hasConditionValues).map((condition) => condition.config),
      fields: listFields.map((field) => field.config),
      ledger,
      settings: dataSettings,
      useQueryLogging: logQueries === true,
    }),
    [conditionFields, dataSettings, ledger, listFields, logQueries, refreshToken, reportId, sessionId]
  );

  const draft = useMemo(
    (): ListPreviewConfiguration => ({
      fields: listFields,
      payload,
      validity: isListConfigurationValid(conditionFields, listFields),
    }),
    [conditionFields, listFields, payload]
  );
  // Keep the previous draft while equivalent: a save round-trip re-mints equal objects minus undefined keys.
  const [draftConfiguration, setDraftConfiguration] = useState(draft);
  if (draftConfiguration !== draft && !areConfigurationsEqual(draftConfiguration, draft)) {
    setDraftConfiguration(draft);
  }

  const exportRequestConfig = useMemo(
    (): BuildListReportRequest => ({
      reportId: reportId ?? "",
      sessionId,
      conditions: conditionFields.filter(hasConditionValues).map((condition) => condition.config),
      fields: listFields.map((field) => field.config),
      ledger,
      sort: sortFields.map((field) => field.config),
      settings,
      range: {},
      useQueryLogging: logQueries === true,
    }),
    [conditionFields, ledger, listFields, logQueries, reportId, sessionId, settings, sortFields]
  );

  const validity = draftConfiguration.validity;
  const aggregationSettingsBuildKey = listFields
    .filter((field) => field.config.hideAggregation !== undefined)
    .map((field) => `${field.config.guid}:${field.config.hideAggregation}`)
    .join(",");
  const settingsBuildKey = `${dataSettings.showGrandTotal}:${aggregationSettingsBuildKey}`;
  const immediateBuildKey = `${reportId}:${sessionId}:${refreshToken}:${validity.valid}:${ledger ?? "auto"}:${settingsBuildKey}`;
  const { deferredValue: activeConfiguration, buildProgress: deferredBuildProgress } = useDeferredRequest(
    draftConfiguration,
    validity.valid,
    immediateBuildKey,
    (applied) =>
      isAdding &&
      applied.payload.refreshToken === refreshToken &&
      applied.payload.reportId === reportId &&
      applied.payload.sessionId === sessionId
  );
  const buildProgress = areConfigurationsEqual(activeConfiguration.payload, draftConfiguration.payload)
    ? undefined
    : deferredBuildProgress;

  useEffect(() => {
    dispatch(createCurrentReportAction("updateBuildProgress", buildProgress));
  }, [buildProgress, dispatch]);

  return { activeConfiguration, validity, buildProgress, exportRequestConfig };
};

export default useListReportConfiguration;
